import { useState } from 'react';
import {
    BodySmallEmphasized,
    BodySmallRegular,
    XStack,
    YStack,
} from 'smoothie';

interface SpanData {
    spanId: string;
    name: string;
    spanType?: string;
    startedAt?: Date | string | null;
    endedAt?: Date | string | null;
    error?: unknown;
    input?: unknown;
    output?: unknown;
    attributes?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
}

interface SpanDetailPanelProps {
    span: SpanData;
    onClose: () => void;
}

export const SpanDetailPanel = ({ span, onClose }: SpanDetailPanelProps) => {
    const duration =
        span.startedAt != null && span.endedAt != null
            ? new Date(span.endedAt).getTime() -
              new Date(span.startedAt).getTime()
            : null;

    return (
        <YStack gap={16} flex={1} padding={16} overflow="auto">
            <XStack justifyContent="space-between" alignItems="center">
                <BodySmallEmphasized color="$gray1200">
                    {span.name}
                </BodySmallEmphasized>
                <BodySmallRegular
                    color="$gray1000"
                    cursor="pointer"
                    onPress={onClose}
                    style={{ fontSize: 16 }}
                >
                    ✕
                </BodySmallRegular>
            </XStack>

            <YStack gap={10}>
                <DetailRow label="Span ID" value={span.spanId} mono />
                <DetailRow label="Type" value={span.spanType ?? 'unknown'} />
                {span.startedAt != null && (
                    <DetailRow
                        label="Started"
                        value={new Date(span.startedAt).toLocaleString()}
                    />
                )}
                {duration != null && (
                    <DetailRow
                        label="Duration"
                        value={formatDuration(duration)}
                    />
                )}
            </YStack>

            {span.error != null && (
                <CollapsibleSection title="Error" defaultOpen>
                    <CodeBlock content={formatJson(span.error)} isError />
                </CollapsibleSection>
            )}

            {span.input != null && (
                <CollapsibleSection title="Input" defaultOpen>
                    <CodeBlock content={formatJson(span.input)} />
                </CollapsibleSection>
            )}

            {span.output != null && (
                <CollapsibleSection title="Output" defaultOpen>
                    <CodeBlock content={formatJson(span.output)} />
                </CollapsibleSection>
            )}

            {span.attributes != null &&
                Object.keys(span.attributes).length > 0 && (
                    <CollapsibleSection title="Attributes">
                        <CodeBlock content={formatJson(span.attributes)} />
                    </CollapsibleSection>
                )}

            {span.metadata != null && Object.keys(span.metadata).length > 0 && (
                <CollapsibleSection title="Metadata">
                    <CodeBlock content={formatJson(span.metadata)} />
                </CollapsibleSection>
            )}
        </YStack>
    );
};

const DetailRow = ({
    label,
    value,
    mono = false,
}: {
    label: string;
    value: string;
    mono?: boolean;
}) => (
    <XStack gap={12} alignItems="baseline">
        <BodySmallRegular color="$gray1000" width={80}>
            {label}
        </BodySmallRegular>
        <BodySmallEmphasized
            color="$gray1200"
            style={mono ? { fontFamily: 'monospace', fontSize: 12 } : undefined}
            flex={1}
        >
            {value}
        </BodySmallEmphasized>
    </XStack>
);

const CollapsibleSection = ({
    title,
    defaultOpen = false,
    children,
}: {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <YStack gap={8}>
            <XStack
                cursor="pointer"
                onPress={() => {
                    setIsOpen(!isOpen);
                }}
                gap={6}
                alignItems="center"
            >
                <BodySmallRegular color="$gray1000">
                    {isOpen ? '▾' : '▸'}
                </BodySmallRegular>
                <BodySmallEmphasized color="$gray1200">
                    {title}
                </BodySmallEmphasized>
            </XStack>
            {isOpen && children}
        </YStack>
    );
};

const CodeBlock = ({
    content,
    isError = false,
}: {
    content: string;
    isError?: boolean;
}) => (
    <YStack
        backgroundColor={isError ? '$red200' : '$gray100'}
        padding={12}
        borderRadius={8}
        borderWidth={1}
        borderColor={isError ? '$red600' : '$gray300'}
        maxHeight={500}
        overflow="auto"
    >
        <BodySmallRegular
            style={{
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 18,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
            color={isError ? '$red1100' : '$gray1200'}
        >
            {content}
        </BodySmallRegular>
    </YStack>
);

function formatJson(value: unknown): string {
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
}
